/**
 * Mississippi State-Specific Diagnostic Questions
 * All questions reference actual MS regulations, forms, and requirements
 * 
 * REGULATORY FRAMEWORK:
 * - Statutory Authority: Miss. Code Ann. §41-71-1 through §41-71-19
 * - Administrative Rules: 15 Miss. Admin. Code Pt. 16, Subpt. 1, Ch. 46
 *   "Minimum Standards of Operation for Home Health Agencies"
 * 
 * Citation Format: 15 Miss. Admin. Code Pt. 16, Subpt. 1, Ch. 46, Rule XX.X.X
 * 
 * Regulatory Body: Mississippi State Department of Health (MSDH)
 * Division: Health Facilities Licensure and Certification
 * Phone: (601) 576-7400
 * Website: https://msdh.ms.gov/page/30,0,83.html
 * 
 * Key Regulatory Sections:
 * - Rules 46.1-46.6: General provisions, definitions, classification
 * - Rules 46.7-46.14: Licensing procedures
 * - Rules 46.15-46.19: Physical facilities
 * - Rules 46.20-46.26: Governing body, administration, finances
 * - Rules 46.27-46.30: Personnel policies, staff development, ethics
 * - Rules 46.31: Patient rights
 * - Rules 46.32-46.46: Clinical services
 * - Rules 46.47-46.51: Clinical records
 * - Rules 46.52-46.55: Evaluation and quality assurance
 * 
 * NOTE: Previous version incorrectly cited "Subpt. 4" - correct citation is
 * "Subpt. 1, Ch. 46" for home health agency regulations.
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MISSISSIPPI_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'MS-A1',
    itemId: 'A1',
    stateCode: 'MS',
    title: 'MSDH Home Health Agency License Status',
    description: 'Mississippi requires all home health agencies to be licensed by MSDH under Miss. Code Ann. §41-71-1 et seq.',
    severity: 'high',
    regulationCode: 'Rule 46.7.1',
    regulationName: 'Licensure',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov/page/30,0,83.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from MSDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home Health licenses expire June 30 annually per Rule 46.10.6. Check your license status with MSDH Licensure and Certification.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your MSDH license certificate. All licenses expire June 30.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your MSDH Home Health Agency license is current and active per Miss. Code Ann. §41-71-13 and Rule 46.7.1.',
      atRisk: 'Your license expires within 90 days. Mississippi requires renewal applications before June 30 per Rule 46.10.7. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Mississippi without a valid MSDH license violates Miss. Code Ann. §41-71-13. Penalties include fines up to $500 per day and criminal misdemeanor charges per Rule 46.13.2. Contact MSDH immediately at (601) 576-7400.',
      unknown: 'Verify your license status with MSDH Licensure and Certification at (601) 576-7400.',
    },
    stateNotes: [
      'All licenses expire June 30 annually per Rule 46.10.6',
      'Initial licensure requires on-site survey per Rule 46.5.1',
      'License types: Regular or Provisional per Rules 46.7.1-46.7.2',
      'License classifications: Private non-profit, Proprietary, Hospital-based, Governmental per Rule 46.6.1',
      'Must maintain 24-hour availability for patient emergencies',
      'Operating without license is a misdemeanor with $500/day penalty per Rule 46.13.2',
    ],
  },

  {
    id: 'MS-A2',
    itemId: 'A2',
    stateCode: 'MS',
    title: 'Administrator Qualifications (Rule 46.3.1)',
    description: 'Mississippi requires qualified administrator for all home health agencies',
    severity: 'high',
    regulationCode: 'Rule 46.3.1',
    regulationName: 'Administrator',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator who meets MSDH requirements per Rule 46.3.1?',
        inputType: 'checklist',
        options: [
          { value: 'designated', label: 'Designated administrator on file with MSDH per Rule 46.22.1' },
          { value: 'education_option1', label: 'Bachelor\'s degree + 1 year admin experience in home health (within last 3 years)' },
          { value: 'education_option2', label: 'OR: 3+ years admin experience in health field, 1 year in home health (within last 3 years)' },
          { value: 'education_option3', label: 'OR: Incumbent administrator as of July 1, 1981' },
          { value: 'designee', label: 'Written designee appointed for administrator absences per Rule 46.22.2' },
        ],
        required: true,
        helpText: 'Rule 46.3.1 specifies three pathways to qualify as administrator',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet MSDH requirements per Rule 46.3.1 and Rule 46.22.1.',
      atRisk: 'Administrator qualifications may not fully meet MSDH requirements. Review Rule 46.3.1 qualification pathways.',
      nonCompliant: 'Administrator does not meet MSDH qualification requirements per Rule 46.3.1. This is a survey deficiency. Contact MSDH for guidance.',
      unknown: 'Verify administrator qualifications against Rule 46.3.1 requirements.',
    },
    stateNotes: [
      'Three qualification pathways per Rule 46.3.1:',
      '(1) Bachelor\'s degree + 1 year admin experience in home health within last 3 years',
      '(2) 3+ years admin experience in health field with 1 year in home health within last 3 years',
      '(3) Incumbent administrator as of July 1, 1981',
      'Administrator duties defined in Rule 46.22.1',
      'Written designee required for administrator absences per Rule 46.22.2',
    ],
  },

  {
    id: 'MS-A3',
    itemId: 'A3',
    stateCode: 'MS',
    title: 'Director of Nursing/Supervising Nurse (Rules 46.3.12, 46.23.1)',
    description: 'Mississippi requires a qualified supervising nurse for all home health agencies',
    severity: 'high',
    regulationCode: 'Rules 46.3.12, 46.23.1',
    regulationName: 'Supervising Nurse',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified Supervising Nurse/Director of Nursing per Rules 46.3.12 and 46.23.1?',
        inputType: 'checklist',
        options: [
          { value: 'fulltime', label: 'Employed full-time in home health activities per Rule 46.23.1' },
          { value: 'rn_license', label: 'Current Mississippi RN license' },
          { value: 'qual_option1', label: 'BSN + 2 years RN experience' },
          { value: 'qual_option2', label: 'OR: Diploma RN + 2 years RN experience' },
          { value: 'qual_option3', label: 'OR: ADN + 4 years RN experience' },
          { value: 'qual_option4', label: 'OR: Incumbent as of July 1, 1981' },
          { value: 'designee', label: 'Qualified alternate designated in writing per Rule 46.23.1' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervising Nurse qualifications meet MSDH requirements per Rules 46.3.12 and 46.23.1.',
      atRisk: 'Supervising Nurse qualifications may be incomplete. Review Rule 46.3.12 and 46.23.1.',
      nonCompliant: 'Agency lacks qualified Supervising Nurse. This is a critical MSDH requirement per Rule 46.23.1.',
      unknown: 'Verify Supervising Nurse qualifications with MSDH.',
    },
    stateNotes: [
      'Must be employed full-time in home health activities per Rule 46.23.1',
      'Qualification pathways per Rule 46.3.12:',
      '(1) BSN + 2 years RN experience',
      '(2) Diploma RN + 2 years RN experience',
      '(3) ADN + 4 years RN experience',
      '(4) Incumbent as of July 1, 1981',
      'Patient ratio guidelines in Rule 46.23.3',
    ],
  },

  {
    id: 'MS-A4',
    itemId: 'A4',
    stateCode: 'MS',
    title: 'Written Policies and Procedures (Rule 46.25.1)',
    description: 'Mississippi requires specific written policies per Rule 46.25.1',
    severity: 'medium',
    regulationCode: 'Rule 46.25.1',
    regulationName: 'Policies',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Mississippi-required policies does your agency have documented per Rule 46.25.1? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Definition of scope of services offered' },
          { value: 'admission', label: 'Admission and discharge policies' },
          { value: 'medical_direction', label: 'Medical direction and supervision' },
          { value: 'plan_of_treatment', label: 'Plans of treatment' },
          { value: 'staff_qualifications', label: 'Staff qualifications, assignments and responsibilities' },
          { value: 'medication', label: 'Medication administration' },
          { value: 'medical_records', label: 'Medical records' },
          { value: 'patient_safety', label: 'Patient safety and emergency care' },
          { value: 'admin_records', label: 'Administrative records' },
          { value: 'evaluation', label: 'Agency evaluation' },
          { value: 'on_call', label: 'After hours emergency care (on call)' },
          { value: 'patient_rights', label: 'Patient rights policies per Rule 46.31.1' },
          { value: 'lab_specimens', label: 'Collection/storage/submission of referral lab samples' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Mississippi requirements per Rule 46.25.1.',
      atRisk: 'Your policy manual is missing required sections per Rule 46.25.1.',
      nonCompliant: 'Multiple required policies are missing per Rule 46.25.1. Immediately develop written policies.',
      unknown: 'Review your policies against Rule 46.25.1 requirements.',
    },
    stateNotes: [
      'Rule 46.25.1 lists 13 required policy areas',
      'Admission/discharge policies must include specific elements per Rule 46.25.1(3)',
      'Patient rights policies per Rule 46.31.1 must be available to patients',
      'On-call provisions required for after-hours emergency care',
    ],
  },

  {
    id: 'MS-A5',
    itemId: 'A5',
    stateCode: 'MS',
    title: 'Governing Body and Bylaws (Rule 46.21.1)',
    description: 'Mississippi requires organized governing body with bylaws',
    severity: 'high',
    regulationCode: 'Rule 46.21.1',
    regulationName: 'Governing Body',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing body have bylaws that include required elements per Rule 46.21.1?',
        inputType: 'checklist',
        options: [
          { value: 'selection', label: 'Member selection basis, terms of office, duties' },
          { value: 'delegation', label: 'Delegation of responsibilities and accountability' },
          { value: 'meetings', label: 'Frequency of board meetings with minutes' },
          { value: 'personnel', label: 'Personnel policies and organizational chart' },
          { value: 'objectives', label: 'Agency statement of objectives' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body structure and bylaws meet MSDH requirements per Rule 46.21.1.',
      atRisk: 'Governing body bylaws may be incomplete per Rule 46.21.1.',
      nonCompliant: 'Governing body bylaws are deficient per Rule 46.21.1.',
      unknown: 'Review governing body bylaws against Rule 46.21.1.',
    },
    stateNotes: [
      'Rule 46.21.1 specifies required bylaw elements',
      'Board must meet regularly with minutes maintained',
      'Agency statement of objectives required',
      'Personnel policies must be established by governing body',
    ],
  },

  {
    id: 'MS-A6',
    itemId: 'A6',
    stateCode: 'MS',
    title: 'Geographic Service Area (Rule 46.10.2)',
    description: 'Mississippi requires agencies to define and adhere to county-based service areas',
    severity: 'medium',
    regulationCode: 'Rule 46.10.2',
    regulationName: 'Service Area',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Has your agency defined its service area by Mississippi counties with MSDH per Rule 46.10.2?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Geographic area must be expressed in Mississippi counties per Rule 46.3.16',
      },
      {
        id: 'q2',
        question: 'Do you provide services only within your approved county service area?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service area is properly defined by county and adhered to per Rule 46.10.2.',
      atRisk: 'Service area definition may need to be updated with MSDH per Rule 46.10.2.',
      nonCompliant: 'Operating outside approved service area requires Certificate of Need and sub-unit establishment per Rule 46.10.2.',
      unknown: 'Verify service area with MSDH.',
    },
    stateNotes: [
      'Service area defined by Mississippi counties per Rule 46.3.16',
      'Expanding service area requires Certificate of Need per Rule 46.10.2',
      'Sub-units required for services outside main service area',
      'Branch offices allowed within service area per Rule 46.3.3',
    ],
  },

  {
    id: 'MS-A7',
    itemId: 'A7',
    stateCode: 'MS',
    title: 'Emergency Operations Plan (Rule 46.20.1)',
    description: 'Mississippi requires comprehensive emergency preparedness planning',
    severity: 'high',
    regulationCode: 'Rule 46.20.1',
    regulationName: 'Emergency Plan',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your Emergency Operations Plan (EOP) address the 9 critical areas per Rule 46.20.1?',
        inputType: 'checklist',
        options: [
          { value: 'communications', label: '1. Communications and facility status reporting' },
          { value: 'resources', label: '2. Resources and assets' },
          { value: 'safety', label: '3. Safety and security' },
          { value: 'staffing', label: '4. Staffing' },
          { value: 'utilities', label: '5. Utilities' },
          { value: 'clinical', label: '6. Clinical activities' },
          { value: 'fire_drills', label: '7. Fire drills (minimum 2x per year)' },
          { value: 'smoke_detectors', label: '8. Smoke detectors/extinguishers (NFPA 10 and 72)' },
          { value: 'coop', label: '9. Continuity of Operations (COOP) including surge/alternate care sites' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Has your EOP been reviewed by MSDH Office of Emergency Planning and Response?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, current verification on file' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Written evidence of EOP verification required with license renewal per Rule 46.20.1',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 9 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency Operations Plan meets MSDH requirements per Rule 46.20.1.',
      atRisk: 'Emergency Operations Plan may be incomplete per Rule 46.20.1. Given Mississippi hurricane risk, ensure comprehensive planning.',
      nonCompliant: 'Emergency Operations Plan is deficient per Rule 46.20.1. MSDH verification required for license renewal.',
      unknown: 'Review EOP against Rule 46.20.1 requirements.',
    },
    stateNotes: [
      'Must use MSDH EOP Template with "All Hazards" and "Whole Community" approach',
      'EOP must address pandemic, terrorism, and natural disasters',
      'Fire drills required minimum 2x per year',
      'EOP must be exercised and reviewed annually',
      'Written verification from MSDH required for license renewal',
    ],
  },

  {
    id: 'MS-A8',
    itemId: 'A8',
    stateCode: 'MS',
    title: 'Business Entity Registration',
    description: 'Mississippi requires proper business registration with Secretary of State',
    severity: 'high',
    regulationCode: 'Miss. Code Ann. 79-4',
    regulationName: 'Business Entity',
    agencyName: 'Mississippi Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(601) 359-1350',
    agencyUrl: 'https://www.sos.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency properly registered with the Mississippi Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your business registration current (annual report filed)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Business entity registration is current with Mississippi Secretary of State.',
      atRisk: 'Business registration may not be current. File annual report with Secretary of State.',
      nonCompliant: 'Agency is not properly registered in Mississippi. Register with Secretary of State immediately.',
      unknown: 'Verify business registration status with Mississippi Secretary of State.',
    },
    stateNotes: [
      'Business registration required under Miss. Code Ann. 79-4',
      'Annual reports must be filed to maintain good standing',
      'Contact Secretary of State at (601) 359-1350',
      'Online verification at sos.ms.gov',
    ],
  },

  {
    id: 'MS-A9',
    itemId: 'A9',
    stateCode: 'MS',
    title: 'Liability Insurance (Rule 46.27.4)',
    description: 'Mississippi strongly recommends liability insurance coverage',
    severity: 'high',
    regulationCode: 'Rule 46.27.4',
    regulationName: 'Insurance',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain professional liability insurance per Rule 46.27.4?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Rule 46.27.4: "For the protection of owner, administrator, and patients served, it is strongly recommended that every home health agency carry liability insurance coverage."',
      },
      {
        id: 'q2',
        question: 'What is your coverage level?',
        inputType: 'select',
        options: [
          { value: 'adequate', label: '$1 million per occurrence / $3 million aggregate or higher' },
          { value: 'low', label: 'Below recommended levels' },
          { value: 'unsure', label: 'Not sure of coverage levels' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'adequate', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['low', 'unsure'], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Professional liability insurance meets recommended coverage levels.',
      atRisk: 'Insurance coverage may be inadequate. Review policy limits with your insurance provider.',
      nonCompliant: 'No professional liability insurance. While not mandated, Rule 46.27.4 strongly recommends coverage. This creates significant legal risk.',
      unknown: 'Verify insurance coverage levels.',
    },
    stateNotes: [
      'Rule 46.27.4 strongly recommends liability insurance coverage',
      'Protects owner, administrator, and patients per regulations',
      'Industry standard: $1M per occurrence / $3M aggregate',
      'Consider professional liability, general liability, and umbrella coverage',
    ],
  },

  {
    id: 'MS-A10',
    itemId: 'A10',
    stateCode: 'MS',
    title: 'Workers Compensation Insurance',
    description: 'Mississippi requires workers compensation coverage for employers with 5+ employees',
    severity: 'high',
    regulationCode: 'Miss. Code Ann. 71-3',
    regulationName: 'Workers Comp',
    agencyName: 'Mississippi Workers Compensation Commission',
    agencyAbbr: 'MWCC',
    agencyPhone: '(601) 987-4200',
    agencyUrl: 'https://www.mwcc.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain workers compensation insurance?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, current coverage' },
          { value: 'exempt', label: 'Exempt (fewer than 5 employees)' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Mississippi requires workers comp for employers with 5 or more employees',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['yes', 'exempt'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage meets Mississippi requirements.',
      atRisk: 'Verify workers compensation coverage is adequate for your workforce.',
      nonCompliant: 'Missing required workers compensation insurance. Contact MWCC at (601) 987-4200.',
      unknown: 'Verify workers compensation status with Mississippi Workers Compensation Commission.',
    },
    stateNotes: [
      'Required for employers with 5 or more employees per Miss. Code Ann. 71-3',
      'Mississippi Workers Compensation Commission: (601) 987-4200',
      'Website: mwcc.ms.gov',
      'Penalties for non-compliance include fines and criminal charges',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'MS-B1',
    itemId: 'B1',
    stateCode: 'MS',
    title: 'Personnel Records (Rule 46.27.2)',
    description: 'Mississippi requires complete personnel records for all employees',
    severity: 'high',
    regulationCode: 'Rule 46.27.2',
    regulationName: 'Personnel Files',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file per Rule 46.27.2? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application with name, address, SSN, DOB, next of kin' },
          { value: 'qualifications', label: 'Evidence of qualifications' },
          { value: 'references', label: 'Reference checks' },
          { value: 'license', label: 'Current licensure/registration if applicable' },
          { value: 'performance', label: 'Performance evaluations per Rule 46.27.1(5)' },
          { value: 'health', label: 'Evidence of health screening per Rule 46.27.5' },
          { value: 'orientation', label: 'Evidence of orientation per Rule 46.29.1' },
          { value: 'contract', label: 'Contract (if applicable)' },
          { value: 'separation', label: 'Date and reason for separation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Mississippi-required documentation per Rule 46.27.2.',
      atRisk: 'Some personnel files are missing required documents per Rule 46.27.2.',
      nonCompliant: 'Personnel files are missing critical documentation per Rule 46.27.2. Surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against Rule 46.27.2 requirements.',
    },
    stateNotes: [
      'Personnel records must be maintained at each licensed site per Rule 46.27.2',
      'Annual performance evaluations required per Rule 46.27.1(5)',
      'Health screening required before employment and annually per Rule 46.27.5',
      'Records must include date of employment and separation with reason',
    ],
  },

  {
    id: 'MS-B2',
    itemId: 'B2',
    stateCode: 'MS',
    title: 'Criminal Background Checks (Rule 46.27.3)',
    description: 'Mississippi requires fingerprint-based criminal background checks for direct care staff',
    severity: 'high',
    regulationCode: 'Rule 46.27.3',
    regulationName: 'Background Checks',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'For ALL employees providing direct patient care, which requirements do you meet per Rule 46.27.3?',
        inputType: 'checklist',
        options: [
          { value: 'fingerprints', label: 'Fingerprints submitted for criminal history check' },
          { value: 'disciplinary', label: 'Disciplinary check with professional licensing agency' },
          { value: 'before_hire', label: 'Background check submitted before first day of employment' },
          { value: 'disqualification_screen', label: 'Screening for 14 disqualifying felonies per Rule 46.27.3(10)' },
          { value: 'documentation', label: 'Documentation of background check on file' },
        ],
        required: true,
        helpText: 'Per Rule 46.27.3, employees may work pending results if fingerprints have been submitted, but employment is voidable if disqualifying record found',
      },
      {
        id: 'q2',
        question: 'For employees hired before July 1, 2003, do you have signed affidavits on file per Rule 46.27.3(12)?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all pre-2003 employees have signed affidavits' },
          { value: 'na', label: 'N/A - no employees from before July 1, 2003' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['yes', 'na'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check documentation is complete per Rule 46.27.3.',
      atRisk: 'Background check documentation may be incomplete per Rule 46.27.3.',
      nonCompliant: 'Employees are working without proper background clearance per Rule 46.27.3. This violates Miss. Code Ann. §43-11-13.',
      unknown: 'Audit all employee files for background check documentation per Rule 46.27.3.',
    },
    stateNotes: [
      '14 disqualifying felonies per Rule 46.27.3(10): drug possession/sale, murder, manslaughter, armed robbery, rape, sexual battery, sex offenses, child abuse, arson, grand larceny, burglary, gratification of lust, aggravated assault, felonious abuse of vulnerable adult',
      'Employees may work pending results if fingerprints submitted per Rule 46.27.3(9)',
      'Notarized suitability letter valid for 2 years per Rule 46.27.3(17)',
      'Pre-July 2003 employees require signed affidavit per Rule 46.27.3(12)',
      'False affidavit is perjury per Rule 46.27.3(14)',
      'Waiver process available for mitigating circumstances per Rule 46.27.3(15)',
    ],
  },

  {
    id: 'MS-B3',
    itemId: 'B3',
    stateCode: 'MS',
    title: 'Employee Health Screening (Rule 46.27.5)',
    description: 'Mississippi requires health screening before employment and annually',
    severity: 'high',
    regulationCode: 'Rule 46.27.5',
    regulationName: 'Health Screening',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees with patient contact have health screening per Rule 46.27.5?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, prior to employment and annually' },
          { value: 'hire_only', label: 'At hire only, not annually' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'Rule 46.27.5: Health screening by licensed physician or NP/PA required before employment and annually thereafter',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee health screening meets MSDH requirements per Rule 46.27.5.',
      atRisk: 'Annual health screening may not be current for all employees per Rule 46.27.5.',
      nonCompliant: 'Employees are working without required health screening per Rule 46.27.5.',
      unknown: 'Verify health screening status for all patient contact employees.',
    },
  },

  {
    id: 'MS-B4',
    itemId: 'B4',
    stateCode: 'MS',
    title: 'TB Screening (Rule 46.29.1)',
    description: 'Mississippi requires TB testing per CDC and MSDH recommendations',
    severity: 'high',
    regulationCode: 'Rule 46.29.1',
    regulationName: 'TB Screening',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees have TB screening per Rule 46.29.1 and CDC recommendations?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, baseline and routine serial testing per CDC/MSDH' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all employees per Rule 46.29.1.',
      atRisk: 'Some TB screenings may be expired. Review all employee files.',
      nonCompliant: 'Employees are working without valid TB screening per Rule 46.29.1.',
      unknown: 'Verify TB screening status for all employees.',
    },
    stateNotes: [
      'Rule 46.29.1 requires compliance with CDC and MSDH TB recommendations',
      'Baseline employee TB testing required',
      'Routine serial testing required per MSDH guidelines',
    ],
  },

  {
    id: 'MS-B5',
    itemId: 'B5',
    stateCode: 'MS',
    title: 'Professional License Verification',
    description: 'Mississippi requires verification of all professional licenses',
    severity: 'high',
    regulationCode: 'Rule 46.27.2',
    regulationName: 'License Verification',
    agencyName: 'Mississippi Board of Nursing',
    agencyAbbr: 'MSBN',
    agencyPhone: '(601) 957-6300',
    agencyUrl: 'https://www.msbn.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses (RN, LPN, PT, OT, ST) with the appropriate licensing board?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, before hire and tracked for renewal' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No formal verification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification is comprehensive and ongoing.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing tracking.',
      nonCompliant: 'No license verification process creates significant liability.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Current licensure/registration required in personnel file per Rule 46.27.2',
      'RN defined in Rule 46.3.51 - must be licensed in Mississippi',
      'LPN defined in Rule 46.3.24 - must be licensed in Mississippi',
      'PT defined in Rule 46.3.39 - must be licensed in Mississippi',
      'OT defined in Rule 46.3.30 - must be licensed in Mississippi',
      'ST defined in Rule 46.3.56 - must meet CCC requirements and be licensed in Mississippi',
    ],
  },

  {
    id: 'MS-B6',
    itemId: 'B6',
    stateCode: 'MS',
    title: 'Annual Performance Evaluations (Rule 46.27.1)',
    description: 'Mississippi requires annual performance evaluations for all employees',
    severity: 'medium',
    regulationCode: 'Rule 46.27.1(5)',
    regulationName: 'Performance Evals',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct documented annual performance evaluations for all employees per Rule 46.27.1(5)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees annually' },
          { value: 'some', label: 'Some employees or inconsistent timing' },
          { value: 'no', label: 'No formal evaluation process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual performance evaluations are conducted for all employees per Rule 46.27.1(5).',
      atRisk: 'Performance evaluations are inconsistent. Implement systematic annual reviews.',
      nonCompliant: 'No formal performance evaluation process per Rule 46.27.1(5). Implement annual evaluations.',
      unknown: 'Review performance evaluation practices.',
    },
    stateNotes: [
      'Rule 46.27.1(5) requires annual performance evaluations',
      'Evaluations must be documented in personnel file per Rule 46.27.2',
      'Use standardized evaluation forms for consistency',
      'Include job-specific competency assessments',
    ],
  },

  {
    id: 'MS-B7',
    itemId: 'B7',
    stateCode: 'MS',
    title: 'Staffing Pattern Documentation (Rule 46.27.6)',
    description: 'Mississippi requires documented staffing patterns maintained on site',
    severity: 'medium',
    regulationCode: 'Rule 46.27.6',
    regulationName: 'Staffing Patterns',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your staffing pattern documentation meet Rule 46.27.6 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'advance', label: 'Developed at least one week in advance' },
          { value: 'names', label: 'Shows name and position of each staff member' },
          { value: 'patients', label: 'Shows patients to be visited' },
          { value: 'supervisory', label: 'Shows scheduled supervisory visits' },
          { value: 'oncall', label: 'Shows staff on call after office hours' },
          { value: 'updated', label: 'Updated daily to reflect actual activities' },
          { value: 'retained', label: 'Kept on file for one year' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staffing pattern documentation meets Rule 46.27.6 requirements.',
      atRisk: 'Staffing pattern documentation may be incomplete per Rule 46.27.6.',
      nonCompliant: 'Staffing pattern documentation does not meet Rule 46.27.6 requirements.',
      unknown: 'Review staffing pattern requirements per Rule 46.27.6.',
    },
    stateNotes: [
      'Must be developed at least one week in advance per Rule 46.27.6',
      'Show name, position, patients to visit, and scheduled supervisory visits',
      'Update daily to reflect actual activities',
      'Retain staffing patterns on file for one year',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'MS-C1',
    itemId: 'C1',
    stateCode: 'MS',
    title: 'OIG Exclusion List Verification',
    description: 'Federal law requires OIG exclusion list verification for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly checks' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed monthly.',
      atRisk: 'OIG verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG exclusion verification. This can result in severe federal penalties including $10,000+ per service provided by excluded individual.',
      unknown: 'Verify OIG exclusion list checking procedures.',
    },
    stateNotes: [
      'Federal requirement under 42 CFR 1001.1901',
      'OIG LEIE database: https://exclusions.oig.hhs.gov',
      'Penalties exceed $10,000 per service by excluded individual',
      'Document all checks with date, names searched, and results',
    ],
  },

  {
    id: 'MS-C2',
    itemId: 'C2',
    stateCode: 'MS',
    title: 'SAM.gov Exclusion Verification',
    description: 'Federal law requires SAM.gov exclusion verification',
    severity: 'high',
    regulationCode: '2 CFR 180',
    regulationName: 'SAM Exclusion',
    agencyName: 'General Services Administration',
    agencyAbbr: 'GSA',
    agencyPhone: '(866) 606-8220',
    agencyUrl: 'https://sam.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify SAM.gov exclusion status for employees and contractors?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly verification' },
          { value: 'hire_only', label: 'At hire/contract only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'SAM.gov exclusion verification is performed regularly.',
      atRisk: 'SAM verification should be performed more frequently.',
      nonCompliant: 'No SAM exclusion verification. Implement verification process.',
      unknown: 'Verify SAM.gov checking procedures.',
    },
    stateNotes: [
      'Required under 2 CFR 180 for federal program participants',
      'SAM.gov database: https://sam.gov',
      'Check employees, contractors, and vendors',
      'Document all searches with date and results',
    ],
  },

  // ============================================
  // SECTION D: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'MS-D1',
    itemId: 'D1',
    stateCode: 'MS',
    title: 'Orientation Training (Rule 46.29.1)',
    description: 'Mississippi requires thorough orientation for all employees',
    severity: 'high',
    regulationCode: 'Rule 46.29.1',
    regulationName: 'Orientation',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your orientation program cover all required topics per Rule 46.29.1?',
        inputType: 'checklist',
        options: [
          { value: 'position', label: 'Thorough orientation to employee\'s position' },
          { value: 'organization', label: 'Agency organization, policies, and objectives' },
          { value: 'functions', label: 'Functions of other agency health personnel and interrelationships' },
          { value: 'community', label: 'Relationship to other community agencies' },
          { value: 'ethics', label: 'Standards of ethical practice' },
          { value: 'confidentiality', label: 'Confidentiality' },
          { value: 'patient_rights', label: 'Patient rights' },
          { value: 'tb', label: 'TB testing and education per CDC/MSDH' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation program meets Rule 46.29.1 requirements.',
      atRisk: 'Orientation program may be missing required topics per Rule 46.29.1.',
      nonCompliant: 'Orientation program is deficient per Rule 46.29.1.',
      unknown: 'Review orientation program against Rule 46.29.1 requirements.',
    },
    stateNotes: [
      'Rule 46.29.1 requires thorough orientation to position',
      'Must cover agency organization, policies, objectives',
      'Include TB testing and education per CDC/MSDH guidelines',
      'Document completion in personnel file',
    ],
  },

  {
    id: 'MS-D2',
    itemId: 'D2',
    stateCode: 'MS',
    title: 'Home Health Aide Training Program (Rule 46.29.2)',
    description: 'Mississippi requires aide training program meeting Medicare requirements',
    severity: 'high',
    regulationCode: 'Rule 46.29.2',
    regulationName: 'HHA Training',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your home health aide training program meet Rule 46.29.2 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'approved', label: 'Training program approved by MSDH' },
          { value: 'duration', label: 'Meets Medicare requirements for duration (75 hours)' },
          { value: 'faculty', label: 'Faculty includes RN for personal care, plus specialists as needed' },
          { value: 'role', label: 'Covers role of home health aide as team member' },
          { value: 'personal_care', label: 'Instruction and supervised practice in personal care' },
          { value: 'nutrition', label: 'Nutrition principles and meal preparation including special diets' },
          { value: 'aging', label: 'Information on aging process and behavior of aged' },
          { value: 'emotional', label: 'Emotional problems accompanying illness' },
          { value: 'environment', label: 'Maintaining clean, healthy, safe environment' },
          { value: 'reporting', label: 'What to report to supervisor' },
          { value: 'recordkeeping', label: 'Record keeping' },
          { value: 'before_care', label: 'Completed before provision of services' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training program meets Rule 46.29.2 requirements (75 hours, MSDH approved).',
      atRisk: 'Training program may be incomplete per Rule 46.29.2.',
      nonCompliant: 'Home health aides are providing care without required training per Rule 46.29.2.',
      unknown: 'Review training program against Rule 46.29.2 requirements.',
    },
    stateNotes: [
      'Training must be completed BEFORE provision of services',
      'Must meet Medicare requirements for duration and subject matter (75 hours)',
      'Training program must be approved by MSDH',
      'Faculty must include RN for personal care training',
      'Required topics listed in Rule 46.29.2(1-9)',
    ],
  },

  {
    id: 'MS-D3',
    itemId: 'D3',
    stateCode: 'MS',
    title: 'Annual In-Service Training (Rule 46.29.3)',
    description: 'Mississippi requires 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: 'Rule 46.29.3',
    regulationName: 'In-Service Training',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL full-time and part-time nurses and home health aides complete 12 hours of in-service training annually per Rule 46.29.3?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your in-service training directly related to home health care per Rule 46.29.3?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training program meets Mississippi 12-hour requirement per Rule 46.29.3.',
      atRisk: 'Some staff may be behind on annual training. Mississippi requires 12 hours annually per Rule 46.29.3.',
      nonCompliant: 'No structured annual training program per Rule 46.29.3. Implement immediately.',
      unknown: 'Review annual training program against Rule 46.29.3 requirements.',
    },
    stateNotes: [
      '12 hours per year required for full-time and part-time nurses and home health aides',
      'Training must be directly related to home health care',
      'Designed to improve skills of staff in direct patient care',
    ],
  },

  {
    id: 'MS-D4',
    itemId: 'D4',
    stateCode: 'MS',
    title: 'Training Documentation (Rule 46.29.4)',
    description: 'Mississippi requires written records of all training programs',
    severity: 'medium',
    regulationCode: 'Rule 46.29.4',
    regulationName: 'Training Records',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your training documentation meet Rule 46.29.4 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'content', label: 'Content of all programs documented' },
          { value: 'attendance', label: 'Attendance records maintained' },
          { value: 'times', label: 'Beginning and ending times recorded' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training documentation meets Rule 46.29.4 requirements.',
      atRisk: 'Training documentation may be incomplete per Rule 46.29.4.',
      nonCompliant: 'Training documentation is deficient per Rule 46.29.4.',
      unknown: 'Review training documentation against Rule 46.29.4.',
    },
    stateNotes: [
      'Rule 46.29.4 requires written records of all training',
      'Document content, attendance, beginning and ending times',
      'Maintain as administrative records',
      'Use sign-in sheets for attendance verification',
    ],
  },

  {
    id: 'MS-D5',
    itemId: 'D5',
    stateCode: 'MS',
    title: 'Patient Rights Training (Rule 46.31.1)',
    description: 'Mississippi requires annual in-service on patient rights',
    severity: 'high',
    regulationCode: 'Rule 46.31.1',
    regulationName: 'Patient Rights',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive annual training on patient rights per Rule 46.31.1?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, annual training for all staff' },
          { value: 'some', label: 'Some staff, not consistent' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Rule 46.31.1: "In-service on patient\'s rights and responsibilities shall be conducted annually."',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual patient rights training is provided per Rule 46.31.1.',
      atRisk: 'Patient rights training is not consistent for all staff.',
      nonCompliant: 'No annual patient rights training per Rule 46.31.1.',
      unknown: 'Verify patient rights training is conducted annually.',
    },
    stateNotes: [
      'Annual in-service on patient rights required per Rule 46.31.1',
      'Include all patient rights listed in Rule 46.31.1(1-8)',
      'Document training attendance and content',
      'Part of 12-hour annual in-service requirement',
    ],
  },

  // ============================================
  // SECTION E: CARE DELIVERY
  // ============================================
  {
    id: 'MS-E1',
    itemId: 'E1',
    stateCode: 'MS',
    title: 'Plan of Treatment (Rules 46.32.1-46.32.3)',
    description: 'Mississippi requires physician-ordered plans of treatment',
    severity: 'high',
    regulationCode: 'Rules 46.32.1-46.32.3',
    regulationName: 'Plan of Treatment',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all patients have a physician-signed plan of treatment per Rule 46.32.1?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before services begin' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of treatment include all required elements per Rule 46.32.2?',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'Diagnoses relevant to home health services' },
          { value: 'limitations', label: 'Functional limitations and rehabilitation potential' },
          { value: 'prognosis', label: 'Prognosis' },
          { value: 'services', label: 'Services authorized including frequency and duration' },
          { value: 'medications', label: 'Medications with dosage, route, frequency' },
          { value: 'treatment', label: 'Treatment with modality, frequency, duration' },
          { value: 'allergies', label: 'Drug and food allergies' },
          { value: 'activities', label: 'Activities permitted' },
          { value: 'diet', label: 'Diet' },
          { value: 'procedures', label: 'Specific procedures for health and safety' },
          { value: 'signature', label: 'Physician signature' },
          { value: 'goals', label: 'Long term goals and discharge plans' },
          { value: 'mental_status', label: 'Mental status' },
          { value: 'equipment', label: 'Equipment required' },
        ],
      },
      {
        id: 'q3',
        question: 'Are plans reviewed by physician at least every 60 days per Rule 46.32.3?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 12 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 8 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of treatment meet MSDH requirements per Rules 46.32.1-46.32.3.',
      atRisk: 'Care plans may be incomplete or not reviewed timely per Rules 46.32.1-46.32.3.',
      nonCompliant: 'Services are being provided without proper physician-ordered care plans per Rule 46.32.1.',
      unknown: 'Review care plan process against Rules 46.32.1-46.32.3.',
    },
    stateNotes: [
      'Rule 46.32.2 lists 14 required elements in plan of treatment',
      'Review required at least every 60 days per Rule 46.32.3',
      'No medication/treatment/service without signed order per Rule 46.32.1',
      'Physician must be consulted for any additions/modifications per Rule 46.32.3',
    ],
  },

  {
    id: 'MS-E2',
    itemId: 'E2',
    stateCode: 'MS',
    title: 'Patient Care Plan (Rules 46.33.1-46.33.2)',
    description: 'Mississippi requires patient care plan based on assessment',
    severity: 'high',
    regulationCode: 'Rules 46.33.1-46.33.2',
    regulationName: 'Care Plan',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your patient care plan documentation meet Rules 46.33.1-46.33.2?',
        inputType: 'checklist',
        options: [
          { value: 'rn_initial', label: 'Initial assessment for skilled nursing by RN per Rule 46.33.1' },
          { value: 'problems', label: 'Patient problems identified per Rule 46.33.2' },
          { value: 'goals', label: 'Anticipated goals and time frames per Rule 46.33.2' },
          { value: 'approaches', label: 'Approaches documented per Rule 46.33.2' },
          { value: 'discipline', label: 'Discipline responsible identified per Rule 46.33.2' },
          { value: 'updated', label: 'Updated at least every 60 days per Rule 46.33.1' },
          { value: 'permanent', label: 'Maintained as permanent part of patient record' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient care plans meet Rules 46.33.1-46.33.2 requirements.',
      atRisk: 'Patient care plans may be incomplete per Rules 46.33.1-46.33.2.',
      nonCompliant: 'Patient care plan documentation is deficient per Rules 46.33.1-46.33.2.',
      unknown: 'Review care plan process against Rules 46.33.1-46.33.2.',
    },
    stateNotes: [
      'Initial assessment for skilled nursing by RN per Rule 46.33.1',
      'Care plan must identify problems, goals, approaches, and responsible discipline',
      'Update at least every 60 days per Rule 46.33.1',
      'Maintain as permanent part of patient record',
    ],
  },

  {
    id: 'MS-E3',
    itemId: 'E3',
    stateCode: 'MS',
    title: 'RN Supervision of Home Health Aides (Rule 46.38.2)',
    description: 'Mississippi requires RN supervisory visits for home health aides',
    severity: 'high',
    regulationCode: 'Rule 46.38.2(6)',
    regulationName: 'Aide Supervision',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do RNs conduct supervisory visits for home health aides per Rule 46.38.2(6)?',
        inputType: 'select',
        options: [
          { value: 'skilled', label: 'Yes - every other week (aide alternately present/absent) for skilled patients' },
          { value: 'aide_only', label: 'Yes - every 60 days for aide-only patients (while aide furnishing care)' },
          { value: 'sometimes', label: 'Sometimes, but not on schedule' },
          { value: 'no', label: 'No regular supervisory visits' },
        ],
        required: true,
        helpText: 'Rule 46.38.2(6): For skilled patients, RN visits every other week with aide alternately present/absent. For aide-only patients, RN visit every 60 days while aide is present.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['skilled', 'aide_only'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervisory visits are conducted per Rule 46.38.2(6) requirements.',
      atRisk: 'Supervisory visits are not consistent. Rule 46.38.2(6) requires every other week for skilled patients, every 60 days for aide-only patients.',
      nonCompliant: 'Home health aides are not receiving required RN supervision per Rule 46.38.2(6).',
      unknown: 'Verify supervision schedule against Rule 46.38.2(6) requirements.',
    },
    stateNotes: [
      'For patients receiving skilled services: RN visit every other week, aide alternately present and absent',
      'For aide-only patients: RN visit at least every 60 days while aide is furnishing care',
      'Supervision may be provided by therapist when appropriate',
      'Home health aides may only perform tasks in written assignment by health professional',
    ],
  },

  {
    id: 'MS-E4',
    itemId: 'E4',
    stateCode: 'MS',
    title: 'RN Supervision of LPNs (Rule 46.35.2)',
    description: 'Mississippi requires RN supervision of licensed practical nurses',
    severity: 'high',
    regulationCode: 'Rule 46.35.2(8)',
    regulationName: 'LPN Supervision',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do RNs provide direct supervision of LPNs per Rule 46.35.2(8)?',
        inputType: 'checklist',
        options: [
          { value: 'monthly', label: 'RN supervisory visit in home of each patient seen by LPN at least monthly' },
          { value: 'accessible', label: 'RN accessible by telecommunications while LPN treating patients' },
          { value: 'documented', label: 'Supervision documented in clinical record' },
        ],
        required: true,
        helpText: 'Rule 46.35.2(8): Direct supervision of LPN in home of each patient at least once a month. LPN not required to be present but RN must be accessible by telecommunications.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'LPN supervision meets Rule 46.35.2(8) requirements.',
      atRisk: 'LPN supervision may not meet all requirements per Rule 46.35.2(8).',
      nonCompliant: 'LPN supervision is deficient per Rule 46.35.2(8).',
      unknown: 'Verify LPN supervision against Rule 46.35.2(8).',
    },
    stateNotes: [
      'RN must supervise LPN in patient home at least monthly per Rule 46.35.2(8)',
      'LPN not required to be present during supervisory visit',
      'RN must be accessible by telecommunications during LPN patient visits',
      'Document supervision in clinical record',
    ],
  },

  {
    id: 'MS-E5',
    itemId: 'E5',
    stateCode: 'MS',
    title: 'Patient Rights (Rule 46.31.1)',
    description: 'Mississippi requires written patient rights policies and notification',
    severity: 'high',
    regulationCode: 'Rule 46.31.1',
    regulationName: 'Patient Rights Notice',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your patient rights program meet Rule 46.31.1 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'informed', label: 'Patient informed of rights prior to or at admission (written acknowledgment)' },
          { value: 'services_charges', label: 'Informed of services and charges before and during treatment' },
          { value: 'participate', label: 'Opportunity to participate in treatment planning' },
          { value: 'transfer', label: 'Advance notice of transfer/discharge with documentation' },
          { value: 'grievances', label: 'May voice grievances free from reprisal' },
          { value: 'confidentiality', label: 'Confidential treatment of records' },
          { value: 'dignity', label: 'Treated with consideration, respect, dignity' },
          { value: 'nondiscrimination', label: 'No refusal of service based on protected class per Rule 46.31.1(8)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights program meets Rule 46.31.1 requirements.',
      atRisk: 'Patient rights program may be incomplete per Rule 46.31.1.',
      nonCompliant: 'Patient rights program is deficient per Rule 46.31.1.',
      unknown: 'Review patient rights program against Rule 46.31.1.',
    },
    stateNotes: [
      'Written acknowledgment required from patient per Rule 46.31.1(1)',
      'Protected classes per Rule 46.31.1(8): age, race, religious preference, sex, marital status, national origin',
      'Annual in-service on patient rights required for staff',
    ],
  },

  // ============================================
  // SECTION F: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'MS-F1',
    itemId: 'F1',
    stateCode: 'MS',
    title: 'Clinical Records (Rules 46.47.1-46.48.1)',
    description: 'Mississippi requires complete clinical documentation',
    severity: 'high',
    regulationCode: 'Rules 46.47.1-46.48.1',
    regulationName: 'Clinical Records',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do your clinical records contain all required elements per Rule 46.48.1?',
        inputType: 'checklist',
        options: [
          { value: 'identifying', label: 'Identifying info for patient, household, caretakers, diagnoses, history, findings' },
          { value: 'pot', label: 'Plan of treatment' },
          { value: 'assessments', label: 'Initial and periodic assessments performed in home' },
          { value: 'care_plan', label: 'Patient care plan' },
          { value: 'clinical_notes', label: 'Clinical notes signed/dated for each contact, written same day, filed weekly' },
          { value: 'conferences', label: 'Case conference reports at least every 60 days' },
          { value: 'progress', label: 'Progress notes at least every 60 days' },
          { value: 'supervisory', label: 'Documentation of supervisory visits' },
          { value: 'discharge', label: 'Discharge summary' },
          { value: 'transfer', label: 'Transfer information sheet if applicable' },
          { value: 'aide_instructions', label: 'Home health aide written instructions' },
          { value: 'verbal_orders', label: 'Verbal orders by RN/professional, countersigned by physician' },
          { value: 'lab', label: 'Duplicate copies of lab results' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per Rules 46.47.1-46.48.1.',
      atRisk: 'Clinical records may be missing required elements per Rule 46.48.1.',
      nonCompliant: 'Clinical records are incomplete per Rule 46.48.1. This is a common survey deficiency.',
      unknown: 'Audit clinical records against Rule 46.48.1 requirements.',
    },
    stateNotes: [
      'Clinical notes must be written same day of service per Rule 46.48.1(5)',
      'Notes must be incorporated into record at least weekly',
      'Case conferences required at least every 60 days per Rule 46.48.1(6)',
      'Verbal orders only by RN or health professionals per Rule 46.48.1(12)',
      'Approved abbreviations list must be maintained per Rule 46.47.1',
    ],
  },

  {
    id: 'MS-F2',
    itemId: 'F2',
    stateCode: 'MS',
    title: 'Record Retention (Rule 46.50.1)',
    description: 'Mississippi requires minimum 5-year record retention',
    severity: 'medium',
    regulationCode: 'Rule 46.50.1',
    regulationName: 'Record Retention',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records per Rule 46.50.1?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer following discharge (meets requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Rule 46.50.1: Clinical records preserved for not less than 5 years following discharge',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention policy meets Mississippi\'s 5-year requirement per Rule 46.50.1.',
      atRisk: 'Mississippi requires 5-year record retention per Rule 46.50.1. Update your policy.',
      nonCompliant: 'Your record retention does not meet Mississippi\'s 5-year requirement per Rule 46.50.1.',
      unknown: 'Review record retention requirements per Rule 46.50.1.',
    },
    stateNotes: [
      'Minimum 5 years following discharge per Rule 46.50.1',
      'May be reproduced on microfilm or other acceptable medium',
      'If agency ceases operation, must arrange for record preservation and notify licensing agency',
    ],
  },

  {
    id: 'MS-F3',
    itemId: 'F3',
    stateCode: 'MS',
    title: 'Confidentiality (Rule 46.49.1)',
    description: 'Mississippi requires confidentiality procedures for patient information',
    severity: 'high',
    regulationCode: 'Rule 46.49.1',
    regulationName: 'Confidentiality',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency meet Rule 46.49.1 confidentiality requirements?',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written confidentiality policies and procedures' },
          { value: 'locked', label: 'Records stored in locked area' },
          { value: 'authorized', label: 'Only authorized personnel have access' },
          { value: 'consent', label: 'Records released only with written patient consent or as required by law' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Confidentiality procedures meet Rule 46.49.1 requirements.',
      atRisk: 'Confidentiality procedures may be incomplete per Rule 46.49.1.',
      nonCompliant: 'Confidentiality procedures are deficient per Rule 46.49.1.',
      unknown: 'Review confidentiality procedures against Rule 46.49.1.',
    },
    stateNotes: [
      'Rule 46.49.1 requires written confidentiality policies',
      'Records must be stored in locked area with authorized access only',
      'Release only with written patient consent or as required by law',
      'Also comply with HIPAA 45 CFR Parts 160-164',
    ],
  },

  {
    id: 'MS-F4',
    itemId: 'F4',
    stateCode: 'MS',
    title: 'Agency Evaluation/QA Program (Rules 46.52.1-46.54.2)',
    description: 'Mississippi requires annual overall agency evaluation and clinical record review',
    severity: 'medium',
    regulationCode: 'Rules 46.52.1-46.54.2',
    regulationName: 'QA Program',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your QA program meet Rules 46.52.1-46.54.2 requirements?',
        inputType: 'checklist',
        options: [
          { value: 'written_policies', label: 'Written policies requiring annual overall evaluation per Rule 46.52.1' },
          { value: 'policy_review', label: 'Policy and administrative review per Rule 46.53.1' },
          { value: 'record_review', label: 'Quarterly clinical record review (active and closed) per Rule 46.54.1' },
          { value: 'sample_size', label: '10% of census reviewed (minimum 10, maximum 50) per Rule 46.54.1' },
          { value: 'appropriateness', label: 'Reviews assess appropriateness, adequacy, effectiveness, efficiency' },
          { value: 'continuing', label: 'Continuing review every 60 days per Rule 46.54.2' },
          { value: 'reported', label: 'Results reported to and acted upon by management' },
          { value: 'admin_records', label: 'Maintained as administrative records' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QA program meets Rules 46.52.1-46.54.2 requirements.',
      atRisk: 'QA program may be incomplete per Rules 46.52.1-46.54.2.',
      nonCompliant: 'QA program is deficient per Rules 46.52.1-46.54.2.',
      unknown: 'Review QA program against Rules 46.52.1-46.54.2.',
    },
    stateNotes: [
      'Quarterly clinical record review required per Rule 46.54.1',
      'Review 10% of census (minimum 10, maximum 50 records)',
      'Records reviewed must represent all services including branch offices',
      'Rule 46.54.1 specifies three review criteria for record review',
      'Continuing review every 60 days per Rule 46.54.2',
    ],
  },

  {
    id: 'MS-F5',
    itemId: 'F5',
    stateCode: 'MS',
    title: 'Standards of Ethical Practice (Rule 46.30.1)',
    description: 'Mississippi requires written standards of ethical practice',
    severity: 'medium',
    regulationCode: 'Rule 46.30.1',
    regulationName: 'Ethics',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written ethical standards per Rule 46.30.1?',
        inputType: 'checklist',
        options: [
          { value: 'adopted', label: 'Governing body has adopted written ethical standards' },
          { value: 'posted', label: 'Posted in each agency office' },
          { value: 'no_misleading', label: 'Prohibition on misleading patients about services/charges' },
          { value: 'no_misappropriation', label: 'Prohibition on misappropriating patient property' },
          { value: 'no_recruit', label: 'Prohibition on recruiting patients from other agencies' },
          { value: 'no_coercion', label: 'No coercion for fundraising participation' },
          { value: 'professional_referrals', label: 'Professional referral acceptance (no remuneration)' },
          { value: 'no_falsification', label: 'Prohibition on falsifying records' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Ethical standards meet Rule 46.30.1 requirements.',
      atRisk: 'Ethical standards may be incomplete per Rule 46.30.1.',
      nonCompliant: 'Ethical standards are deficient per Rule 46.30.1.',
      unknown: 'Review ethical standards against Rule 46.30.1.',
    },
    stateNotes: [
      'Governing body must adopt written ethical standards per Rule 46.30.1',
      'Must be posted in each agency office',
      'Include prohibitions on misleading patients, misappropriating property, false records',
      'Professional referrals must not involve remuneration',
    ],
  },

  {
    id: 'MS-F6',
    itemId: 'F6',
    stateCode: 'MS',
    title: 'Contract Services Documentation (Rule 46.28.1)',
    description: 'Mississippi requires written contracts for services provided under arrangement',
    severity: 'medium',
    regulationCode: 'Rule 46.28.1',
    regulationName: 'Contracts',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'If you use contract staff, do your contracts include required elements per Rule 46.28.1?',
        inputType: 'checklist',
        options: [
          { value: 'specification', label: 'Specification of services covered' },
          { value: 'dates_terms', label: 'Effective date, length, terms of reimbursement' },
          { value: 'acceptance', label: 'Statement that patients accepted only by home health agency' },
          { value: 'pot_statement', label: 'Statement that services per plan of treatment only' },
          { value: 'quality', label: 'Quality of services and personnel qualifications consistent with agency' },
          { value: 'supervision', label: 'Identification of parties responsible for supervision' },
          { value: 'information', label: 'Procedures for exchanging patient care information' },
          { value: 'na', label: 'N/A - no contract staff used' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['na'], result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contract documentation meets Rule 46.28.1 requirements.',
      atRisk: 'Contract documentation may be incomplete per Rule 46.28.1.',
      nonCompliant: 'Contract documentation is deficient per Rule 46.28.1.',
      unknown: 'Review contract documentation against Rule 46.28.1.',
    },
    stateNotes: [
      'Rule 46.28.1 specifies 7 required contract elements',
      'Only home health agency may accept patients',
      'Services must be per plan of treatment only',
      'Identify parties responsible for supervision and information exchange',
    ],
  },

  {
    id: 'MS-F7',
    itemId: 'F7',
    stateCode: 'MS',
    title: 'Termination of Operation (Rule 46.14.1)',
    description: 'Mississippi requires specific procedures if agency ceases operation',
    severity: 'medium',
    regulationCode: 'Rule 46.14.1',
    regulationName: 'Termination',
    agencyName: 'Mississippi State Department of Health',
    agencyAbbr: 'MSDH',
    agencyPhone: '(601) 576-7400',
    agencyUrl: 'https://msdh.ms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have documented procedures for agency termination per Rule 46.14.1?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, procedures include all required elements' },
          { value: 'no', label: 'No documented procedures' },
        ],
        required: true,
        helpText: 'Rule 46.14.1 requires: (1) Advance notice to physician and patient, (2) Transfer of records, (3) Public announcement, (4) 7-year record storage, (5) Return license to MSDH',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Termination procedures documented per Rule 46.14.1.',
      atRisk: 'No documented termination procedures. Review Rule 46.14.1 requirements.',
      nonCompliant: 'Termination procedures missing per Rule 46.14.1.',
      unknown: 'Review termination requirements per Rule 46.14.1.',
    },
    stateNotes: [
      'Note: Rule 46.14.1(4) requires 7-year record storage following discharge',
      'This differs from standard 5-year retention in Rule 46.50.1',
      'When agency terminates, the longer 7-year period applies',
    ],
  },
];

export default MISSISSIPPI_QUESTIONS;